import { Color, SRGBColorSpace } from 'three';

import {
  type FbxEmbeddedContent,
  type FbxMaterialTextureSet,
  type ParsedFbxConnectionRecord,
  type ParsedFbxMaterialRecord,
  type ParsedFbxTextureRecord,
  type ParsedFbxVideoRecord,
  buildFbxMaterialTextureSets,
} from './fbxUdim';

export interface FbxExtractedData {
  colors: Map<string, Color>;
  /** FBX texture-node IDs whose backing Video has no embedded Content.
   *  These reference external files and will fail to load from a blob URL. */
  externalTextureIds: Set<number>;
  /** Material texture groups recovered from Texture/Video/Connections nodes. */
  udimTextureSets: FbxMaterialTextureSet[];
}

/**
 * Lightweight FBX parser that extracts material data directly from the raw
 * binary (or ASCII) file data:
 *
 *  - **Diffuse colors**: Three.js FBXLoader drops DiffuseColor values whose
 *    property type isn't "Color" / "ColorRGB" (e.g. "Vector3D" from Maya).
 *    We read them regardless of type so materials can be patched afterward.
 *
 *  - **External texture detection**: FBX Video nodes that lack embedded
 *    Content reference external files. When the FBX is loaded from a blob URL
 *    those textures will always 404. We report their texture-node IDs so the
 *    material correction pass can strip them upfront instead of rendering black.
 *
 *  - **UDIM metadata**: Texture / Video / Connections nodes are grouped into
 *    per-material texture sets so the correction pass can rebuild embedded
 *    UDIM atlases when the FBX contains them.
 */
export function extractFbxMaterialColors(
  buffer: ArrayBuffer,
): FbxExtractedData {
  try {
    if (isBinaryFbx(buffer)) return parseBinaryFbxData(buffer);

    return {
      colors: parseAsciiFbxColors(buffer),
      externalTextureIds: new Set(),
      udimTextureSets: [],
    };
  } catch (err) {
    console.warn('[Cartographer FBX] Material data extraction failed:', err);
    return {
      colors: new Map(),
      externalTextureIds: new Set(),
      udimTextureSets: [],
    };
  }
}

// ---------------------------------------------------------------------------
// Format detection
// ---------------------------------------------------------------------------

const FBX_MAGIC = 'Kaydara FBX Binary';

function isBinaryFbx(buffer: ArrayBuffer): boolean {
  if (buffer.byteLength < 27) return false;
  const view = new DataView(buffer);
  for (let i = 0; i < FBX_MAGIC.length; i += 1) {
    if (view.getUint8(i) !== FBX_MAGIC.charCodeAt(i)) return false;
  }
  return true;
}

// ---------------------------------------------------------------------------
// Tiny DataView reader
// ---------------------------------------------------------------------------

class BufReader {
  private readonly v: DataView;
  pos: number;

  constructor(buffer: ArrayBuffer) {
    this.v = new DataView(buffer);
    this.pos = 0;
  }

  get length() {
    return this.v.byteLength;
  }

  u8() {
    return this.v.getUint8(this.pos++);
  }

  i16() {
    const result = this.v.getInt16(this.pos, true);
    this.pos += 2;
    return result;
  }

  u32() {
    const result = this.v.getUint32(this.pos, true);
    this.pos += 4;
    return result;
  }

  i32() {
    const result = this.v.getInt32(this.pos, true);
    this.pos += 4;
    return result;
  }

  f32() {
    const result = this.v.getFloat32(this.pos, true);
    this.pos += 4;
    return result;
  }

  f64() {
    const result = this.v.getFloat64(this.pos, true);
    this.pos += 8;
    return result;
  }

  /** Read uint64 as a JS number (safe for file offsets < 2^53). */
  u64n() {
    const lo = this.u32();
    const hi = this.u32();
    return hi * 0x100000000 + lo;
  }

  str(len: number) {
    let value = '';
    for (let i = 0; i < len; i += 1) value += String.fromCharCode(this.u8());
    return value;
  }

  bytes(len: number) {
    const result = new Uint8Array(
      this.v.buffer,
      this.v.byteOffset + this.pos,
      len,
    );
    this.pos += len;
    return result;
  }

  skip(n: number) {
    this.pos += n;
  }
}

// ---------------------------------------------------------------------------
// Binary FBX node / property reader
// ---------------------------------------------------------------------------

interface NodeHeader {
  name: string;
  endOffset: number;
  numProps: number;
  propsEnd: number;
}

interface RawBinaryProp {
  kind: 'raw';
  bytes: Uint8Array;
}

interface ArrayMetaProp {
  kind: 'array';
  type: string;
  count: number;
  encoding: number;
  compressedLength: number;
}

type Prop = string | number | boolean | RawBinaryProp | ArrayMetaProp | null;

function readNodeHeader(r: BufReader, is64: boolean): NodeHeader | null {
  if (r.pos + (is64 ? 25 : 13) > r.length) return null;

  const endOffset = is64 ? r.u64n() : r.u32();
  if (endOffset === 0) return null;

  const numProps = is64 ? r.u64n() : r.u32();
  const propsLen = is64 ? r.u64n() : r.u32();
  const nameLen = r.u8();
  const name = r.str(nameLen);

  return { name, endOffset, numProps, propsEnd: r.pos + propsLen };
}

function isRawBinaryProp(value: Prop | undefined): value is RawBinaryProp {
  return (
    value != null && typeof value === 'object' && value.kind === 'raw'
  );
}

function readProp(r: BufReader): Prop {
  const type = String.fromCharCode(r.u8());
  switch (type) {
    case 'C':
      return r.u8() !== 0;
    case 'Y':
      return r.i16();
    case 'I':
      return r.i32();
    case 'F':
      return r.f32();
    case 'D':
      return r.f64();
    case 'L':
      return r.u64n();
    case 'S': {
      const len = r.u32();
      return r.str(len);
    }
    case 'R': {
      const len = r.u32();
      return { kind: 'raw', bytes: r.bytes(len) };
    }
    case 'd':
    case 'f':
    case 'i':
    case 'l':
    case 'b': {
      const count = r.u32();
      const encoding = r.u32();
      const compressedLength = r.u32();
      r.skip(compressedLength);
      return {
        kind: 'array',
        type,
        count,
        encoding,
        compressedLength,
      };
    }
    default:
      throw new Error(`Unknown FBX property type '${type}'`);
  }
}

/**
 * Read node properties, recovering to propsEnd on unknown types.
 */
function readProps(r: BufReader, n: number, propsEnd: number): Prop[] {
  const out: Prop[] = [];
  for (let i = 0; i < n; i += 1) {
    if (r.pos >= propsEnd) break;
    try {
      out.push(readProp(r));
    } catch {
      r.pos = propsEnd;
      break;
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Binary: full extraction
// ---------------------------------------------------------------------------

function parseBinaryFbxData(buffer: ArrayBuffer): FbxExtractedData {
  const r = new BufReader(buffer);
  const colors = new Map<string, Color>();
  const materials = new Map<number, ParsedFbxMaterialRecord>();
  const textures = new Map<number, ParsedFbxTextureRecord>();
  const videos = new Map<number, ParsedFbxVideoRecord>();
  const connections: ParsedFbxConnectionRecord[] = [];

  r.skip(23);
  const version = r.u32();
  const is64 = version >= 7500;

  while (r.pos < r.length) {
    const node = readNodeHeader(r, is64);
    if (!node) break;

    if (node.name === 'Objects') {
      r.pos = node.propsEnd;
      walkObjects(r, node.endOffset, is64, colors, materials, textures, videos);
    } else if (node.name === 'Connections') {
      r.pos = node.propsEnd;
      walkConnections(r, node.endOffset, is64, connections);
    }

    r.pos = node.endOffset;
  }

  const externalTextureIds = new Set<number>();
  for (const connection of connections) {
    const video = videos.get(connection.childId);
    if (!video || video.content) continue;
    if (textures.has(connection.parentId)) {
      externalTextureIds.add(connection.parentId);
    }
  }

  return {
    colors,
    externalTextureIds,
    udimTextureSets: buildFbxMaterialTextureSets(
      materials,
      textures,
      videos,
      connections,
    ),
  };
}

// ---------------------------------------------------------------------------
// Objects walker: Material (colors) + Texture/Video metadata
// ---------------------------------------------------------------------------

function walkObjects(
  r: BufReader,
  endOffset: number,
  is64: boolean,
  colors: Map<string, Color>,
  materials: Map<number, ParsedFbxMaterialRecord>,
  textures: Map<number, ParsedFbxTextureRecord>,
  videos: Map<number, ParsedFbxVideoRecord>,
): void {
  while (r.pos < endOffset) {
    const child = readNodeHeader(r, is64);
    if (!child) break;

    if (child.name === 'Material') {
      parseMaterialNode(r, child, is64, colors, materials);
    } else if (child.name === 'Texture') {
      parseTextureNode(r, child, is64, textures);
    } else if (child.name === 'Video') {
      parseVideoNode(r, child, is64, videos);
    }

    r.pos = child.endOffset;
  }
}

function parseMaterialNode(
  r: BufReader,
  matNode: NodeHeader,
  is64: boolean,
  colors: Map<string, Color>,
  materials: Map<number, ParsedFbxMaterialRecord>,
): void {
  const props = readProps(r, matNode.numProps, matNode.propsEnd);
  const materialId = typeof props[0] === 'number' ? props[0] : 0;
  const materialName = typeof props[1] === 'string' ? props[1] : '';
  if (materialId) {
    materials.set(materialId, { id: materialId, name: materialName });
  }

  if (!materialName) return;
  const color = findDiffuseInMaterial(r, matNode, is64);
  if (color) {
    colors.set(materialName, color);
  }
}

function parseTextureNode(
  r: BufReader,
  textureNode: NodeHeader,
  is64: boolean,
  textures: Map<number, ParsedFbxTextureRecord>,
): void {
  const props = readProps(r, textureNode.numProps, textureNode.propsEnd);
  const textureId = typeof props[0] === 'number' ? props[0] : 0;
  if (!textureId) return;

  const texture: ParsedFbxTextureRecord = {
    id: textureId,
    name: typeof props[1] === 'string' ? props[1] : '',
    fileName: null,
    relativeFileName: null,
  };

  r.pos = textureNode.propsEnd;
  while (r.pos < textureNode.endOffset) {
    const child = readNodeHeader(r, is64);
    if (!child) break;

    const childProps = readProps(r, child.numProps, child.propsEnd);
    if (child.name === 'FileName' && typeof childProps[0] === 'string') {
      texture.fileName = childProps[0];
    } else if (
      child.name === 'RelativeFilename' &&
      typeof childProps[0] === 'string'
    ) {
      texture.relativeFileName = childProps[0];
    }

    r.pos = child.endOffset;
  }

  textures.set(textureId, texture);
}

function parseVideoNode(
  r: BufReader,
  videoNode: NodeHeader,
  is64: boolean,
  videos: Map<number, ParsedFbxVideoRecord>,
): void {
  const props = readProps(r, videoNode.numProps, videoNode.propsEnd);
  const videoId = typeof props[0] === 'number' ? props[0] : 0;
  if (!videoId) return;

  const video: ParsedFbxVideoRecord = {
    id: videoId,
    name: typeof props[1] === 'string' ? props[1] : '',
    fileName: null,
    relativeFileName: null,
    content: null,
  };

  r.pos = videoNode.propsEnd;
  while (r.pos < videoNode.endOffset) {
    const child = readNodeHeader(r, is64);
    if (!child) break;

    const childProps = readProps(r, child.numProps, child.propsEnd);
    if (child.name === 'Filename' && typeof childProps[0] === 'string') {
      video.fileName = childProps[0];
    } else if (
      child.name === 'RelativeFilename' &&
      typeof childProps[0] === 'string'
    ) {
      video.relativeFileName = childProps[0];
    } else if (child.name === 'Content') {
      video.content = parseEmbeddedContent(childProps[0]);
    }

    r.pos = child.endOffset;
  }

  videos.set(videoId, video);
}

function parseEmbeddedContent(
  value: Prop | undefined,
): FbxEmbeddedContent | null {
  if (typeof value === 'string' && value !== '') {
    return { kind: 'base64', data: value };
  }
  if (isRawBinaryProp(value) && value.bytes.byteLength > 0) {
    return { kind: 'binary', bytes: value.bytes };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Connections walker
// ---------------------------------------------------------------------------

function walkConnections(
  r: BufReader,
  endOffset: number,
  is64: boolean,
  connections: ParsedFbxConnectionRecord[],
): void {
  while (r.pos < endOffset) {
    const child = readNodeHeader(r, is64);
    if (!child) break;

    if (child.name === 'C' && child.numProps >= 3) {
      const props = readProps(r, child.numProps, child.propsEnd);
      const childId = typeof props[1] === 'number' ? props[1] : 0;
      const parentId = typeof props[2] === 'number' ? props[2] : 0;
      if (childId && parentId) {
        connections.push({
          childId,
          parentId,
          relationship: typeof props[3] === 'string' ? props[3] : '',
        });
      }
    }

    r.pos = child.endOffset;
  }
}

// ---------------------------------------------------------------------------
// Material color helpers (shared by binary walker)
// ---------------------------------------------------------------------------

function findDiffuseInMaterial(
  r: BufReader,
  matNode: NodeHeader,
  is64: boolean,
): Color | null {
  r.pos = matNode.propsEnd;

  while (r.pos < matNode.endOffset) {
    const sub = readNodeHeader(r, is64);
    if (!sub) break;

    if (sub.name === 'Properties70') {
      r.pos = sub.propsEnd;
      return scanProperties70(r, sub.endOffset, is64);
    }

    r.pos = sub.endOffset;
  }
  return null;
}

/**
 * Scan P-nodes for the diffuse color, accepting any type label.
 * Priority: DiffuseColor > Diffuse > Maya|baseColor (standardSurface).
 */
function scanProperties70(
  r: BufReader,
  endOffset: number,
  is64: boolean,
): Color | null {
  let diffuse: Color | null = null;
  let mayaBaseColor: Color | null = null;

  while (r.pos < endOffset) {
    const p = readNodeHeader(r, is64);
    if (!p) break;

    if (p.name === 'P' && p.numProps >= 5) {
      const props = readProps(r, p.numProps, p.propsEnd);
      const propName = props[0];

      if (propName === 'DiffuseColor' || propName === 'Diffuse') {
        const nums = props.filter(
          (value): value is number => typeof value === 'number',
        );
        if (nums.length >= 3) {
          const color = new Color().setRGB(
            nums[0],
            nums[1],
            nums[2],
            SRGBColorSpace,
          );
          if (propName === 'DiffuseColor') return color;
          diffuse = color;
        }
      } else if (propName === 'Maya|baseColor') {
        const nums = props.filter(
          (value): value is number => typeof value === 'number',
        );
        if (nums.length >= 3) {
          mayaBaseColor = new Color().setRGB(
            nums[0],
            nums[1],
            nums[2],
            SRGBColorSpace,
          );
        }
      }
    }

    r.pos = p.endOffset;
  }

  return diffuse ?? mayaBaseColor;
}

// ---------------------------------------------------------------------------
// ASCII FBX fallback (colors only; external-texture detection not supported)
// ---------------------------------------------------------------------------

function parseAsciiFbxColors(buffer: ArrayBuffer): Map<string, Color> {
  const text = new TextDecoder().decode(buffer);
  const result = new Map<string, Color>();

  const matRe = /Material:\s*\d+\s*,\s*"([^"]*)"[^{]*\{/g;
  let match: RegExpExecArray | null;

  while ((match = matRe.exec(text)) !== null) {
    const rawName = match[1];
    const blockStart = match.index + match[0].length;

    let depth = 1;
    let blockEnd = blockStart;
    while (depth > 0 && blockEnd < text.length) {
      if (text[blockEnd] === '{') depth += 1;
      if (text[blockEnd] === '}') depth -= 1;
      blockEnd += 1;
    }

    const block = text.substring(blockStart, blockEnd);
    const colorRe =
      /P:\s*"(DiffuseColor|Diffuse|Maya\|baseColor)"(?:\s*,\s*"[^"]*"){3}\s*,\s*([\d.eE+-]+)\s*,\s*([\d.eE+-]+)\s*,\s*([\d.eE+-]+)/g;

    let best: { priority: number; r: number; g: number; b: number } | null =
      null;
    let colorMatch: RegExpExecArray | null;
    while ((colorMatch = colorRe.exec(block)) !== null) {
      const priority =
        colorMatch[1] === 'DiffuseColor'
          ? 0
          : colorMatch[1] === 'Diffuse'
            ? 1
            : 2;
      const rValue = parseFloat(colorMatch[2]);
      const gValue = parseFloat(colorMatch[3]);
      const bValue = parseFloat(colorMatch[4]);
      if (
        Number.isFinite(rValue) &&
        Number.isFinite(gValue) &&
        Number.isFinite(bValue) &&
        (!best || priority < best.priority)
      ) {
        best = {
          priority,
          r: rValue,
          g: gValue,
          b: bValue,
        };
      }
    }

    if (best) {
      result.set(
        rawName,
        new Color().setRGB(best.r, best.g, best.b, SRGBColorSpace),
      );
    }
  }

  return result;
}
